/**
 * SettingsPanel — Right panel. v2
 * Shows either newsletter-global settings OR selected section settings.
 * Each section type has its own settings sub-panel.
 */
import React from 'react';
import axios from 'axios';
import { Settings, Smartphone, Monitor, Trash2, Plus, GripVertical, Image as ImageIcon, FileText, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { useEditorStore } from '../stores/editorStore';
import { API_URL, UPLOAD_URL } from '../../../api/api';
import { resolveAssetUrl } from '../lib/assetUrl';

// ── Shared UI primitives ──────────────────────────────────────────────────────

const Label = ({ children }) => (
  <label style={{
    display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--em-fg5)',
    marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5,
  }}>
    {children}
  </label>
);

const Field = ({ children, style }) => (
  <div style={{ marginBottom: 14, ...style }}>{children}</div>
);

const Input = ({ value, onChange, type = 'text', min, max, step, placeholder }) => (
  <input
    type={type} value={value ?? ''} min={min} max={max} step={step}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%', padding: '6px 10px', fontSize: 13,
      border: '1px solid var(--em-border)', borderRadius: 6, outline: 'none',
      boxSizing: 'border-box',
    }}
  />
);

const Select = ({ value, onChange, options }) => (
  <select
    value={value ?? ''}
    onChange={(e) => onChange(e.target.value)}
    style={{
      width: '100%', padding: '6px 10px', fontSize: 13,
      border: '1px solid var(--em-border)', borderRadius: 6, outline: 'none',
      background: 'var(--em-bg-card)', boxSizing: 'border-box',
    }}
  >
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
    ))}
  </select>
);

const ColorPicker = ({ value, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <input
      type="color" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)}
      style={{ width: 32, height: 32, padding: 2, border: '1px solid var(--em-border)', borderRadius: 6, cursor: 'pointer' }}
    />
    <input
      type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
      style={{ flex: 1, padding: '5px 8px', fontSize: 12, border: '1px solid var(--em-border)', borderRadius: 6 }}
      placeholder="#ffffff"
    />
  </div>
);

const SectionLabel = ({ children }) => (
  <p style={{
    fontSize: 11, fontWeight: 700, color: 'var(--em-fg5)',
    textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px',
  }}>
    {children}
  </p>
);

const FONTS  = ['Inter, sans-serif', 'Arial, sans-serif', 'Georgia, serif', 'Times New Roman, serif', 'Courier New, monospace'];
const ALIGNS = [
  { value: 'left', label: 'Left' }, { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' }, { value: 'justify', label: 'Justify' },
];

// ── Newsletter-wide settings ──────────────────────────────────────────────────

function NewsletterSettings() {
  const { meta, updateMeta, title, setTitle } = useEditorStore();
  return (
    <div>
      <p style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
        Newsletter Settings
      </p>
      <Field><Label>Title</Label><Input value={title} onChange={setTitle} /></Field>
      <Field><Label>Subject Line</Label><Input value={meta.subject} onChange={(v) => updateMeta({ subject: v })} placeholder="Email subject…" /></Field>
      <Field><Label>Preview Text</Label><Input value={meta.previewText} onChange={(v) => updateMeta({ previewText: v })} placeholder="Short preview text…" /></Field>
      <Field>
        <Label>Content Width (px)</Label>
        <Input type="number" value={meta.contentWidth} min={400} max={1200} step={10} onChange={(v) => updateMeta({ contentWidth: Number(v) })} />
      </Field>
      <Field><Label>Background Colour</Label><ColorPicker value={meta.backgroundColor} onChange={(v) => updateMeta({ backgroundColor: v })} /></Field>
      <Field><Label>Accent Colour</Label><ColorPicker value={meta.accentColor} onChange={(v) => updateMeta({ accentColor: v })} /></Field>
      <Field>
        <Label>Default Font</Label>
        <Select value={meta.fontFamily} onChange={(v) => updateMeta({ fontFamily: v })} options={FONTS.map((f) => ({ value: f, label: f.split(',')[0] }))} />
      </Field>
    </div>
  );
}

// ── Common section settings (background, text, font, padding, align) ──────────

function CommonSettings({ section, setSetting }) {
  const st = section.settings || {};
  return (
    <>
      <Field><Label>Background</Label><ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} /></Field>
      <Field><Label>Text Colour</Label><ColorPicker value={st.textColor} onChange={setSetting('textColor')} /></Field>
      <Field><Label>Accent Colour</Label><ColorPicker value={st.accentColor} onChange={setSetting('accentColor')} /></Field>
      <Field>
        <Label>Font Size</Label>
        <Input value={(st.fontSize || '16px').replace('px', '')} type="number" min={10} max={72}
          onChange={(v) => setSetting('fontSize')(`${v}px`)} />
      </Field>
      <Field><Label>Padding</Label><Input value={st.padding} onChange={setSetting('padding')} placeholder="16px" /></Field>
    </>
  );
}

// ── Per-type settings panels ──────────────────────────────────────────────────

function HeaderSettings({ section, setSetting, updateSection }) {
  const data = section.data || {};
  const st   = section.settings || {};

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const logoFileRef   = React.useRef(null);
  const bannerFileRef = React.useRef(null);

  const handleUpload = async (file, field) => {
    if (!file) return;
    const form  = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const r = await axios.post(`${API_URL}/api/newsletters/assets/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateData({ [field]: `${UPLOAD_URL}${r.data.url}` });
    } catch { /* silent */ }
  };

  return (
    <>
      {/* ── Content fields ───────────────────────────────────── */}
      <Field>
        <Label>Title</Label>
        <Input value={data.title ?? ''} onChange={(v) => updateData({ title: v })} placeholder="Newsletter Title" />
      </Field>
      <Field>
        <Label>Subtitle</Label>
        <Input value={data.subtitle ?? ''} onChange={(v) => updateData({ subtitle: v })} placeholder="April 2026" />
      </Field>

      {/* ── Logo ─────────────────────────────────────────────── */}
      <Field>
        <Label>Logo</Label>
        {data.logoUrl ? (
          <div style={{ marginBottom: 6 }}>
            <img
              src={resolveAssetUrl(data.logoUrl)}
              alt="Logo"
              style={{
                width: '100%', maxHeight: 80, objectFit: 'contain',
                border: '1px solid var(--em-border)', borderRadius: 6, padding: 4,
                background: 'var(--em-bg-head)', cursor: 'pointer',
              }}
              onClick={() => logoFileRef.current?.click()}
              title="Click to replace"
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button
                onClick={() => logoFileRef.current?.click()}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid var(--em-border)', borderRadius: 5, cursor: 'pointer', background: 'var(--em-bg-head)', color: '#374151' }}
              >
                Replace
              </button>
              <button
                onClick={() => updateData({ logoUrl: null })}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer', background: '#fff5f5', color: '#ef4444' }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => logoFileRef.current?.click()}
            style={{
              width: '100%', padding: '10px 0', fontSize: 12,
              border: '1.5px dashed #d1d5db', borderRadius: 6,
              cursor: 'pointer', background: 'var(--em-bg-head)', color: 'var(--em-fg5)',
            }}
          >
            + Upload Logo
          </button>
        )}
        <input
          ref={logoFileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { handleUpload(e.target.files?.[0], 'logoUrl'); e.target.value = ''; }}
        />
      </Field>

      {/* ── Banner Image ──────────────────────────────────────── */}
      <Field>
        <Label>Banner Image</Label>
        {data.bannerImageUrl ? (
          <div>
            <img
              src={resolveAssetUrl(data.bannerImageUrl)}
              alt="Banner"
              style={{ width: '100%', height: 60, objectFit: 'cover', borderRadius: 6, marginBottom: 4 }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => bannerFileRef.current?.click()}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid var(--em-border)', borderRadius: 5, cursor: 'pointer', background: 'var(--em-bg-head)', color: '#374151' }}
              >
                Replace
              </button>
              <button
                onClick={() => updateData({ bannerImageUrl: null })}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer', background: '#fff5f5', color: '#ef4444' }}
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 4, padding: '14px 8px', cursor: 'pointer',
            border: '1.5px dashed #d1d5db', borderRadius: 6,
            background: '#fafafa', color: '#9ca3af', fontSize: 11, textAlign: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/>
              <path d="m21 15-5-5L5 21"/>
            </svg>
            Drag &amp; drop or <span style={{ color: '#f54b01', fontWeight: 600 }}>click to upload</span>
            <span style={{ color: '#d1d5db' }}>Recommended: 700×200</span>
            <input
              ref={bannerFileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { handleUpload(e.target.files?.[0], 'bannerImageUrl'); e.target.value = ''; }}
            />
          </label>
        )}
      </Field>

      {/* ── Section Colours ───────────────────────────────────── */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field style={{ marginBottom: 0 }}>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor} onChange={setSetting('textColor')} />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} />
        </Field>
      </div>

      {/* ── Typography ───────────────────────────────────────── */}
      <SectionLabel>Typography</SectionLabel>
      <Field>
        <Label>Title Font Size (px)</Label>
        <Input type="number" value={st.titleSize ?? 26} min={14} max={60}
          onChange={(v) => setSetting('titleSize')(Number(v))} />
      </Field>
      <Field>
        <Label>Subtitle Font Size (px)</Label>
        <Input type="number" value={st.subtitleSize ?? 13} min={10} max={24}
          onChange={(v) => setSetting('subtitleSize')(Number(v))} />
      </Field>
      <Field>
        <Label>Font Family</Label>
        <Select
          value={st.fontFamily ?? 'Georgia, serif'}
          onChange={setSetting('fontFamily')}
          options={FONTS.map((f) => ({ value: f, label: f.split(',')[0] }))}
        />
      </Field>
      <Field>
        <Label>Padding</Label>
        <Input value={st.padding} onChange={setSetting('padding')} placeholder="14px 24px" />
      </Field>
    </>
  );
}

function FoundersFocusSettings({ section, setSetting, updateSection }) {
  const data = section.data     || {};
  const st   = section.settings || {};

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const align = st.textAlign || 'left';

  const ALIGN_OPTIONS = [
    { value: 'left',   label: 'Left'   },
    { value: 'center', label: 'Center' },
    { value: 'right',  label: 'Right'  },
  ];

  return (
    <>
      {/* Quote textarea */}
      <Field>
        <Label>Quote</Label>
        <textarea
          value={data.quote ?? ''}
          onChange={(e) => updateData({ quote: e.target.value })}
          placeholder="Write the founder's quote here…"
          rows={5}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 13,
            border: '1px solid var(--em-border)', borderRadius: 6, outline: 'none',
            resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.6,
          }}
        />
      </Field>

      {/* Name */}
      <Field>
        <Label>Name</Label>
        <Input value={data.name ?? 'Bertin Dcruz'} onChange={(v) => updateData({ name: v })} placeholder="Bertin Dcruz" />
      </Field>

      {/* Designation */}
      <Field>
        <Label>Designation</Label>
        <Input value={data.designation ?? 'Founder and CEO, Electronikmedia'} onChange={(v) => updateData({ designation: v })} placeholder="Founder and CEO, Electronikmedia" />
      </Field>

      {/* Alignment toggle */}
      <Field>
        <Label>Alignment</Label>
        <div style={{ display: 'flex', gap: 6 }}>
          {ALIGN_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setSetting('textAlign')(value)}
              style={{
                flex: 1, padding: '7px 0', fontSize: 12, fontWeight: 500,
                border: '1px solid var(--em-border)', borderRadius: 6, cursor: 'pointer',
                background: align === value ? '#111827' : '#f9fafb',
                color:      align === value ? '#ffffff' : '#374151',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}
            >
              <svg width="12" height="10" viewBox="0 0 12 10" fill="currentColor">
                <rect x="0" y="0" width="12" height="1.5" rx="0.75"/>
                <rect x="0" y="4" width={value === 'left' ? 9 : value === 'right' ? 9 : 12} height="1.5" rx="0.75"
                  x={value === 'right' ? 3 : 0}/>
                <rect x="0" y="8" width="12" height="1.5" rx="0.75"/>
              </svg>
              {label}
            </button>
          ))}
        </div>
      </Field>

      {/* Section Colors */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field style={{ marginBottom: 0 }}>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor} onChange={setSetting('textColor')} />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} />
        </Field>
      </div>
    </>
  );
}

function MeetEngineerSettings({ section, setSetting, updateSection }) {
  const data = section.data     || {};
  const st   = section.settings || {};

  const qna = data.qna || [];

  const [employees, setEmployees] = React.useState([]);
  const photoFileRef = React.useRef(null);

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  // Fetch employee list once
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    axios
      .get(`${API_URL}/api/team`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleEmployeeSelect = (empId) => {
    const emp = employees.find((e) => String(e.id) === String(empId));
    if (!emp) return;
    const fullName = [emp.first_name, emp.last_name].filter(Boolean).join(' ');
    const photoUrl = emp.profile_photo
      ? emp.profile_photo.startsWith('http') ? emp.profile_photo : `${UPLOAD_URL}${emp.profile_photo}`
      : null;
    updateData({ employeeId: empId, name: fullName, jobTitle: emp.designation || emp.department || '', photoUrl });
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const r = await axios.post(`${API_URL}/api/newsletters/assets/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateData({ photoUrl: `${UPLOAD_URL}${r.data.url}` });
    } catch { /* silent */ }
  };

  const addQnA = () =>
    updateData({ qna: [...qna, { id: `qa-${Date.now()}-${Math.floor(Math.random()*9999)}`, question: '', answer: '' }] });

  const removeQnA = (id) =>
    updateData({ qna: qna.filter((q) => q.id !== id) });

  const updateQnA = (id, field, value) =>
    updateData({ qna: qna.map((q) => (q.id === id ? { ...q, [field]: value } : q)) });

  return (
    <>
      {/* Employee selector */}
      {employees.length > 0 && (
        <Field>
          <Label>Select Employee</Label>
          <select
            value={data.employeeId || ''}
            onChange={(e) => handleEmployeeSelect(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px', fontSize: 13,
              border: '1px solid var(--em-border)', borderRadius: 6, outline: 'none',
              background: 'var(--em-bg-card)', boxSizing: 'border-box',
            }}
          >
            <option value="">— Select employee —</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {[emp.first_name, emp.last_name].filter(Boolean).join(' ')}
                {emp.designation ? ` — ${emp.designation}` : ''}
              </option>
            ))}
          </select>
        </Field>
      )}

      {/* Name */}
      <Field>
        <Label>Name</Label>
        <Input value={data.name || ''} onChange={(v) => updateData({ name: v })} placeholder="Engineer name" />
      </Field>

      {/* Role */}
      <Field>
        <Label>Role</Label>
        <Input value={data.jobTitle || ''} onChange={(v) => updateData({ jobTitle: v })} placeholder="Job title" />
      </Field>

      {/* Photo */}
      <Field>
        <Label>Photo</Label>
        {data.photoUrl ? (
          <>
            <img
              src={resolveAssetUrl(data.photoUrl)}
              alt=""
              style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8, marginBottom: 6, display: 'block' }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => photoFileRef.current?.click()}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid var(--em-border)', borderRadius: 5, cursor: 'pointer', background: 'var(--em-bg-head)', color: '#374151' }}
              >
                Replace
              </button>
              <button
                onClick={() => updateData({ photoUrl: null })}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer', background: '#fff5f5', color: '#ef4444' }}
              >
                Remove
              </button>
            </div>
            <input
              ref={photoFileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { handlePhotoUpload(e.target.files?.[0]); e.target.value = ''; }}
            />
          </>
        ) : (
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            padding: '14px 8px', cursor: 'pointer',
            border: '1.5px dashed #d1d5db', borderRadius: 6,
            background: '#fafafa', color: '#9ca3af', fontSize: 11, textAlign: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            Drag &amp; drop or <span style={{ color: '#f54b01', fontWeight: 600 }}>click to upload</span>
            <input
              type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { handlePhotoUpload(e.target.files?.[0]); e.target.value = ''; }}
            />
          </label>
        )}
      </Field>

      {/* Section Colors */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <Field style={{ marginBottom: 0 }}>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor} onChange={setSetting('textColor')} />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} />
        </Field>
      </div>

      {/* Q&A */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, marginTop: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--em-fg5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Q&amp;A ({qna.length})
        </span>
        <button
          onClick={addQnA}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, fontWeight: 600, color: '#f54b01',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <Plus size={12} /> Add Q&amp;A
        </button>
      </div>

      {qna.map((item) => (
        <div key={item.id} style={{
          border: '1px solid var(--em-border)', borderRadius: 8,
          padding: '10px 12px', marginBottom: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}>Question &amp; Answer</span>
            <button
              onClick={() => removeQnA(item.id)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', padding: 2 }}
            >
              <Trash2 size={13} />
            </button>
          </div>
          <textarea
            value={item.question}
            onChange={(e) => updateQnA(item.id, 'question', e.target.value)}
            placeholder="Question"
            rows={2}
            style={{
              width: '100%', padding: '6px 8px', fontSize: 12,
              border: '1px solid var(--em-border)', borderRadius: 5, outline: 'none',
              resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5, marginBottom: 6,
            }}
          />
          <textarea
            value={item.answer}
            onChange={(e) => updateQnA(item.id, 'answer', e.target.value)}
            placeholder="Answer"
            rows={3}
            style={{
              width: '100%', padding: '6px 8px', fontSize: 12,
              border: '1px solid var(--em-border)', borderRadius: 5, outline: 'none',
              resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5,
            }}
          />
        </div>
      ))}

      {qna.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '12px 8px',
          border: '1.5px dashed var(--em-border)', borderRadius: 8,
          color: '#9ca3af', fontSize: 12,
        }}>
          No Q&amp;As yet — click "+ Add Q&amp;A" above
        </div>
      )}
    </>
  );
}

// ── MemberEditor sub-component (needs its own hooks scope) ───────────────────

function MemberEditor({ member, onUpdate, onRemove, onPhotoUpload, employees }) {
  const photoFileRef    = React.useRef(null);
  const [isPhotoDragOver, setIsPhotoDragOver] = React.useState(false);

  // Auto-fill name + photo from employee list
  const handleEmployeeSelect = (empId) => {
    if (!empId) { onUpdate({ employeeId: null }); return; }
    const emp = (employees || []).find((e) => String(e.id) === String(empId));
    if (!emp) return;
    const fullName = [emp.first_name, emp.last_name].filter(Boolean).join(' ');
    const photoUrl = emp.profile_photo
      ? emp.profile_photo.startsWith('http') ? emp.profile_photo : `${UPLOAD_URL}${emp.profile_photo}`
      : null;
    onUpdate({ employeeId: empId, name: fullName, photoUrl });
  };

  // Photo drag-and-drop
  const handlePhotoDragOver = (e) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault(); e.stopPropagation();
    setIsPhotoDragOver(true);
  };
  const handlePhotoDragLeave = (e) => { e.preventDefault(); setIsPhotoDragOver(false); };
  const handlePhotoDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsPhotoDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) onPhotoUpload(file);
  };

  return (
    <div style={{ padding: '10px 12px', borderTop: '1px solid var(--em-border)' }}>

      {/* ── Employee dropdown ────────────────────────────────── */}
      {employees && employees.length > 0 && (
        <Field>
          <Label>Select Employee</Label>
          <select
            value={member.employeeId || ''}
            onChange={(e) => handleEmployeeSelect(e.target.value)}
            style={{
              width: '100%', padding: '6px 10px', fontSize: 13,
              border: '1px solid var(--em-border)', borderRadius: 6, outline: 'none',
              background: 'var(--em-bg-card)', boxSizing: 'border-box',
            }}
          >
            <option value="">— Select employee —</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {[emp.first_name, emp.last_name].filter(Boolean).join(' ')}
                {emp.designation ? ` — ${emp.designation}` : ''}
              </option>
            ))}
          </select>
        </Field>
      )}

      {/* ── Name (editable override) ─────────────────────────── */}
      <Field>
        <Label>Name</Label>
        <Input
          value={member.name ?? ''}
          onChange={(v) => onUpdate({ name: v })}
          placeholder="Member name"
        />
      </Field>

      {/* ── Photo ────────────────────────────────────────────── */}
      <Field>
        <Label>Photo</Label>
        {member.photoUrl ? (
          <div>
            <img
              src={resolveAssetUrl(member.photoUrl)}
              alt=""
              style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, marginBottom: 6, display: 'block' }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => photoFileRef.current?.click()}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid var(--em-border)', borderRadius: 5, cursor: 'pointer', background: 'var(--em-bg-head)', color: '#374151' }}
              >
                Replace
              </button>
              <button
                onClick={() => onUpdate({ photoUrl: null, employeeId: null })}
                style={{ flex: 1, padding: '5px 0', fontSize: 11, border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer', background: '#fff5f5', color: '#ef4444' }}
              >
                Remove
              </button>
            </div>
            <input
              ref={photoFileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { onPhotoUpload(e.target.files?.[0]); e.target.value = ''; }}
            />
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={() => photoFileRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && photoFileRef.current?.click()}
            onDragOver={handlePhotoDragOver}
            onDragLeave={handlePhotoDragLeave}
            onDrop={handlePhotoDrop}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
              padding: '14px 8px', cursor: 'pointer',
              border: `1.5px dashed ${isPhotoDragOver ? '#f54b01' : '#d1d5db'}`,
              borderRadius: 6,
              background: isPhotoDragOver ? '#fff7ed' : '#fafafa',
              fontSize: 11, textAlign: 'center',
              transition: 'all 0.15s',
              userSelect: 'none',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={isPhotoDragOver ? '#f54b01' : '#d1d5db'}
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            {isPhotoDragOver
              ? <span style={{ color: '#f54b01' }}>↓ Drop photo here</span>
              : <span style={{ color: '#9ca3af' }}>Drag & drop or <span style={{ color: '#f54b01', fontWeight: 600 }}>click to upload</span></span>
            }
            <input
              ref={photoFileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => { onPhotoUpload(e.target.files?.[0]); e.target.value = ''; }}
            />
          </div>
        )}
      </Field>

      {/* ── Card Color ───────────────────────────────────────── */}
      <Field>
        <Label>Card Color</Label>
        <ColorPicker value={member.cardColor || '#e9e0cc'} onChange={(v) => onUpdate({ cardColor: v })} />
      </Field>

      {/* ── Description ──────────────────────────────────────── */}
      <Field>
        <Label>Description</Label>
        <div style={{ border: '1px solid var(--em-border)', borderRadius: 6, overflow: 'hidden' }}>
          <RichTextEditor
            content={member.descriptionHtml || ''}
            onChange={(html) => onUpdate({ descriptionHtml: html })}
            placeholder="Add a description..."
          />
        </div>
      </Field>
    </div>
  );
}

// ── AppreciationSettings ──────────────────────────────────────────────────────

function AppreciationSettings({ section, setSetting, updateSection }) {
  const data    = section.data     || {};
  const st      = section.settings || {};

  const members = data.members ?? [];
  const [expandedId, setExpandedId] = React.useState(null);
  const [employees, setEmployees]   = React.useState([]);

  // Fetch employee list once
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/team`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setEmployees(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const addMember = () => {
    const m = {
      id:              `m-${Date.now()}-${Math.floor(Math.random() * 9999)}`,
      employeeId:      null,
      name:            '',
      photoUrl:        null,
      cardColor:       '#e9e0cc',
      descriptionHtml: '',
    };
    updateData({ members: [...members, m] });
    setExpandedId(m.id);
  };

  const removeMember = (id) => {
    updateData({ members: members.filter((m) => m.id !== id) });
    setExpandedId((prev) => (prev === id ? null : prev));
  };

  const updateMember = (id, patch) =>
    updateData({ members: members.map((m) => (m.id === id ? { ...m, ...patch } : m)) });

  const handlePhotoUpload = async (memberId, file) => {
    if (!file) return;
    const form  = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const r = await axios.post(`${API_URL}/api/newsletters/assets/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateMember(memberId, { photoUrl: `${UPLOAD_URL}${r.data.url}` });
    } catch { /* silent */ }
  };

  const COLUMNS_OPTIONS = [
    { value: 1, label: '1 column'  },
    { value: 2, label: '2 columns' },
    { value: 3, label: '3 columns' },
  ];

  return (
    <>
      {/* Section Colors */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
        <Field style={{ marginBottom: 0 }}>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor} onChange={setSetting('textColor')} />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} />
        </Field>
      </div>

      {/* Members Per Row */}
      <Field>
        <Label>Members Per Row</Label>
        <div style={{ display: 'flex', gap: 4 }}>
          {COLUMNS_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setSetting('columnsPerRow')(value)}
              style={{
                flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 500,
                border: '1px solid var(--em-border)', borderRadius: 6, cursor: 'pointer',
                background: (st.columnsPerRow ?? 2) === value ? '#111827' : '#f9fafb',
                color:      (st.columnsPerRow ?? 2) === value ? '#ffffff' : '#374151',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </Field>

      {/* Members list */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--em-fg5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Members ({members.length})
        </span>
        <button
          onClick={addMember}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, fontWeight: 600, color: '#f54b01',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <Plus size={12} /> Add Member
        </button>
      </div>

      {members.map((m) => {
        const isExpanded = expandedId === m.id;
        return (
          <div
            key={m.id}
            style={{ border: '1px solid var(--em-border)', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}
          >
            {/* Collapsed header row */}
            <div
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 10px', cursor: 'pointer', background: 'var(--em-bg-head)',
              }}
              onClick={() => setExpandedId(isExpanded ? null : m.id)}
            >
              <span style={{ fontSize: 14, color: '#c4c4c4', lineHeight: 1 }}>&#8942;</span>
              <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: '#374151' }}>
                {m.name || 'New Member'}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); removeMember(m.id); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#ef4444', display: 'flex', alignItems: 'center', padding: 2,
                }}
              >
                <Trash2 size={12} />
              </button>
            </div>

            {/* Expanded editor */}
            {isExpanded && (
              <MemberEditor
                member={m}
                onUpdate={(patch) => updateMember(m.id, patch)}
                onRemove={() => removeMember(m.id)}
                onPhotoUpload={(file) => handlePhotoUpload(m.id, file)}
                employees={employees}
              />
            )}
          </div>
        );
      })}

      {members.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '16px 8px',
          border: '1.5px dashed var(--em-border)', borderRadius: 8,
          color: '#9ca3af', fontSize: 12,
        }}>
          No members yet — click "+ Add Member" above
        </div>
      )}
    </>
  );
}

const PROJECT_STATUS_OPTIONS = ['In Progress', 'Completed', 'On Hold'];
const PROJECT_STATUS_DOTS = {
  'In Progress': '#3b82f6',
  'Completed':   '#10b981',
  'On Hold':     '#f59e0b',
};

function ProjectUpdateSettings({ section, setSetting, updateSection }) {
  const data = section.data     || {};
  const st   = section.settings || {};

  const status  = data.status     ?? 'In Progress';
  const html    = data.html       ?? '';
  const columns = st.columnsCount ?? 1;

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const COLS_OPTIONS = [
    { value: 1, label: '1 column'  },
    { value: 2, label: '2 columns' },
    { value: 3, label: '3 columns' },
  ];

  return (
    <>
      {/* Status */}
      <Field>
        <Label>Status</Label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: PROJECT_STATUS_DOTS[status] || '#6b7280',
          }} />
          <select
            value={status}
            onChange={(e) => updateData({ status: e.target.value })}
            style={{
              flex: 1, padding: '6px 10px', fontSize: 13,
              border: '1px solid var(--em-border)', borderRadius: 6,
              outline: 'none', background: 'var(--em-bg-card)', boxSizing: 'border-box',
            }}
          >
            {PROJECT_STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </Field>

      {/* Content Columns */}
      <Field>
        <Label>Content Columns</Label>
        <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
          {COLS_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setSetting('columnsCount')(value)}
              style={{
                flex: 1, padding: '7px 0', fontSize: 11, fontWeight: 500,
                border: '1px solid var(--em-border)', borderRadius: 6, cursor: 'pointer',
                background: columns === value ? '#111827' : '#f9fafb',
                color:      columns === value ? '#ffffff' : '#374151',
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <p style={{ margin: 0, fontSize: 10, color: '#9ca3af' }}>
          Content auto-flows across columns
        </p>
      </Field>

      {/* Content editor */}
      <Field>
        <Label>Content</Label>
        <div style={{ border: '1px solid var(--em-border)', borderRadius: 6, overflow: 'hidden' }}>
          <RichTextEditor
            content={html}
            onChange={(v) => updateData({ html: v })}
            placeholder="Describe the project update..."
          />
        </div>
      </Field>

      {/* Section Colors */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field style={{ marginBottom: 0 }}>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor} onChange={setSetting('textColor')} />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} />
        </Field>
      </div>
    </>
  );
}

const FOOTER_PLATFORMS = ['LinkedIn', 'Instagram', 'Twitter', 'Facebook', 'YouTube', 'Website'];

function FooterSettings({ section, setSetting, updateSection }) {
  const data = section.data     || {};
  const st   = section.settings || {};

  const contentHtml = data.contentHtml ?? '';
  const socialLinks = data.socialLinks ?? [];

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const addLink = () =>
    updateData({
      socialLinks: [
        ...socialLinks,
        { id: `sl-${Date.now()}-${Math.floor(Math.random() * 9999)}`, platform: 'LinkedIn', url: '' },
      ],
    });

  const removeLink = (id) =>
    updateData({ socialLinks: socialLinks.filter((l) => l.id !== id) });

  const updateLink = (id, patch) =>
    updateData({ socialLinks: socialLinks.map((l) => (l.id === id ? { ...l, ...patch } : l)) });

  return (
    <>
      {/* Footer Content */}
      <Field>
        <Label>Footer Content</Label>
        <div style={{ border: '1px solid var(--em-border)', borderRadius: 6, overflow: 'hidden' }}>
          <RichTextEditor
            content={contentHtml}
            onChange={(v) => updateData({ contentHtml: v })}
            placeholder="Add footer content..."
          />
        </div>
      </Field>

      {/* Social Links */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--em-fg5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Social Links
        </span>
        <button
          onClick={addLink}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            fontSize: 11, fontWeight: 600, color: '#f54b01',
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <Plus size={12} /> Add
        </button>
      </div>

      {socialLinks.map((link) => (
        <div key={link.id} style={{
          display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
        }}>
          {/* Drag handle (visual) */}
          <span style={{ fontSize: 14, color: '#c4c4c4', lineHeight: 1, flexShrink: 0 }}>&#8942;</span>

          {/* Platform dropdown */}
          <select
            value={link.platform}
            onChange={(e) => updateLink(link.id, { platform: e.target.value })}
            style={{
              width: 106, padding: '5px 6px', fontSize: 12,
              border: '1px solid var(--em-border)', borderRadius: 5, outline: 'none',
              background: 'var(--em-bg-card)', flexShrink: 0,
            }}
          >
            {FOOTER_PLATFORMS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>

          {/* URL input */}
          <input
            value={link.url}
            onChange={(e) => updateLink(link.id, { url: e.target.value })}
            placeholder="https://..."
            style={{
              flex: 1, padding: '5px 8px', fontSize: 12,
              border: '1px solid var(--em-border)', borderRadius: 5, outline: 'none',
              minWidth: 0,
            }}
          />

          {/* Delete */}
          <button
            onClick={() => removeLink(link.id)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#ef4444', display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0,
            }}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}

      {socialLinks.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '12px 8px', marginBottom: 14,
          border: '1.5px dashed var(--em-border)', borderRadius: 8,
          color: '#9ca3af', fontSize: 12,
        }}>
          No links yet — click "+ Add" above
        </div>
      )}

      {/* Section Colors */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field style={{ marginBottom: 0 }}>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor} onChange={setSetting('textColor')} />
        </Field>
        <Field style={{ marginBottom: 0 }}>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor} onChange={setSetting('backgroundColor')} />
        </Field>
      </div>
    </>
  );
}

// ── Image section settings ────────────────────────────────────────────────────

function ImageSettings({ section, setSetting, updateSection }) {
  const data = section.data     || {};
  const st   = section.settings || {};

  const updateData = (patch) =>
    updateSection(section.id, { data: { ...data, ...patch } });

  const imageFileRef = React.useRef(null);
  const dragItem     = React.useRef(null);
  const [dragOver,        setDragOver]        = React.useState(null);
  const [isImageDragOver, setIsImageDragOver] = React.useState(false);

  // Auto-migrate: sections created before TYPE_DEFAULTS was set have #ffffff
  React.useEffect(() => {
    if (!st.backgroundColor || st.backgroundColor === '#ffffff') {
      setSetting('backgroundColor')('#e9e0cc');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const form  = new FormData();
    form.append('file', file);
    const token = localStorage.getItem('token');
    try {
      const r = await axios.post(`${API_URL}/api/newsletters/assets/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      updateData({ imageUrl: `${UPLOAD_URL}${r.data.url}` });
    } catch { /* silent */ }
  };

  const layout           = data.layout           || ['image', 'caption'];
  const captionAlignment = data.captionAlignment || 'left';

  const handleDragStart = (item) => { dragItem.current = item; };
  const handleDragOver  = (e, item) => { e.preventDefault(); setDragOver(item); };
  const handleDrop      = (e, target) => {
    e.preventDefault();
    const src = dragItem.current;
    if (!src || src === target) { setDragOver(null); return; }
    const newLayout = layout.map((i) => (i === src ? target : i === target ? src : i));
    updateData({ layout: newLayout });
    dragItem.current = null;
    setDragOver(null);
  };
  const handleDragEnd = () => { dragItem.current = null; setDragOver(null); };

  // Image upload zone — only fires for file drags, not layout card drags
  const handleImageZoneDragOver = (e) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault(); e.stopPropagation();
    setIsImageDragOver(true);
  };
  const handleImageZoneDragLeave = (e) => {
    e.preventDefault();
    setIsImageDragOver(false);
  };
  const handleImageZoneDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setIsImageDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <>
      {/* ── Add Image ─────────────────────────────────────────── */}
      <Field>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 8, color: '#111827' }}>●</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>Add Image</span>
        </div>
        {data.imageUrl ? (
          <div style={{ position: 'relative' }}>
            <img
              src={resolveAssetUrl(data.imageUrl)}
              alt="section"
              style={{ width: '100%', borderRadius: 6, maxHeight: 120, objectFit: 'cover', display: 'block' }}
            />
            <button
              onClick={() => updateData({ imageUrl: null })}
              style={{
                position: 'absolute', top: 4, right: 4,
                background: '#ef4444', color: '#fff',
                border: 'none', borderRadius: 4,
                padding: '2px 8px', cursor: 'pointer', fontSize: 11,
              }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            onClick={() => imageFileRef.current?.click()}
            onKeyDown={(e) => e.key === 'Enter' && imageFileRef.current?.click()}
            onDragOver={handleImageZoneDragOver}
            onDragLeave={handleImageZoneDragLeave}
            onDrop={handleImageZoneDrop}
            style={{
              width: '100%', padding: '28px 0',
              background: isImageDragOver ? '#fff7ed' : '#f9fafb',
              border: `2px dashed ${isImageDragOver ? '#f54b01' : '#d1d5db'}`,
              borderRadius: 6, cursor: 'pointer',
              fontSize: 13, color: isImageDragOver ? '#f54b01' : '#6b7280',
              textAlign: 'center', boxSizing: 'border-box',
              transition: 'background 0.15s, border-color 0.15s, color 0.15s',
              userSelect: 'none',
            }}
          >
            {isImageDragOver ? '↓  Drop image here' : 'Drag & drop or click to upload'}
          </div>
        )}
        <input
          ref={imageFileRef} type="file" accept="image/*" style={{ display: 'none' }}
          onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }}
        />
      </Field>

      {/* ── Section Heading ───────────────────────────────────── */}
      <Field>
        <Label>Section Heading</Label>
        <Input
          value={data.heading || ''}
          onChange={(v) => updateData({ heading: v })}
          placeholder="Enter heading…"
        />
      </Field>

      {/* ── Content Layout ────────────────────────────────────── */}
      <SectionLabel>Content Layout</SectionLabel>
      <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10, marginTop: -4 }}>
        Drag to reorder image and caption
      </p>

      {layout.map((item) => (
        <div
          key={item}
          draggable
          onDragStart={() => handleDragStart(item)}
          onDragOver={(e) => handleDragOver(e, item)}
          onDrop={(e) => handleDrop(e, item)}
          onDragEnd={handleDragEnd}
          style={{
            border: `1px solid ${dragOver === item ? '#f54b01' : 'var(--em-border)'}`,
            borderRadius: 8, marginBottom: 10,
            background: 'var(--em-bg-card)', overflow: 'hidden',
            transition: 'border-color 0.15s',
          }}
        >
          {/* Card header row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', background: 'var(--em-bg-head)',
            borderBottom: '1px solid var(--em-border)', cursor: 'grab',
          }}>
            <GripVertical size={14} color="#9ca3af" />
            {item === 'image'
              ? <ImageIcon size={14} color="#374151" />
              : <FileText  size={14} color="#374151" />
            }
            <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              {item === 'image' ? 'Image' : 'Caption'}
            </span>
          </div>

          {/* Card body */}
          {item === 'image' && data.imageUrl && (
            <div style={{ padding: 8 }}>
              <img
                src={resolveAssetUrl(data.imageUrl)}
                alt="preview"
                style={{ width: '100%', borderRadius: 4, maxHeight: 100, objectFit: 'cover', display: 'block' }}
              />
            </div>
          )}
          {item === 'caption' && (
            <div style={{ padding: 8 }}>
              <RichTextEditor
                content={data.captionHtml || ''}
                onChange={(html) => updateData({ captionHtml: html })}
                placeholder="Add a caption…"
              />
            </div>
          )}
        </div>
      ))}

      {/* ── Caption Alignment ─────────────────────────────────── */}
      <Field>
        <Label>Caption Alignment</Label>
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { value: 'left',   Icon: AlignLeft   },
            { value: 'center', Icon: AlignCenter  },
            { value: 'right',  Icon: AlignRight   },
          ].map(({ value, Icon }) => (
            <button
              key={value}
              onClick={() => updateData({ captionAlignment: value })}
              style={{
                flex: 1, padding: '8px 0',
                border: '1px solid var(--em-border)', borderRadius: 6,
                background: captionAlignment === value ? '#111827' : '#f9fafb',
                color:      captionAlignment === value ? '#fff'    : '#374151',
                cursor: 'pointer',
                display: 'flex', justifyContent: 'center', alignItems: 'center',
              }}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </Field>

      {/* ── Section Colors ────────────────────────────────────── */}
      <SectionLabel>Section Colors</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <Label>Font Color</Label>
          <ColorPicker value={st.textColor || '#000000'} onChange={setSetting('textColor')} />
        </div>
        <div>
          <Label>Background</Label>
          <ColorPicker value={st.backgroundColor || '#e9e0cc'} onChange={setSetting('backgroundColor')} />
        </div>
      </div>
    </>
  );
}

// ── Legacy section settings (text, image, hero, cta, columns, divider, spacer)

function LegacySectionSettings({ section, setSetting, updateSection }) {
  const st = section.settings || {};
  const mo = section.mobileOverrides || {};
  const setMobile = (key) => (val) =>
    useEditorStore.getState().updateSectionMobileOverride(section.id, { [key]: val });

  return (
    <>
      <CommonSettings section={section} setSetting={setSetting} />
      <Field>
        <Label>Text Align</Label>
        <Select value={st.textAlign} onChange={setSetting('textAlign')} options={ALIGNS} />
      </Field>
      <Field><Label>Border Radius</Label><Input value={st.borderRadius} onChange={setSetting('borderRadius')} placeholder="0px" /></Field>

      {/* CTA-specific */}
      {section.type === 'cta' && (
        <>
          <SectionLabel>CTA Settings</SectionLabel>
          <Field>
            <Label>Button Label</Label>
            <Input value={section.ctaLabel} onChange={(v) => updateSection(section.id, { ctaLabel: v })} placeholder="Learn More" />
          </Field>
          <Field>
            <Label>Button URL</Label>
            <Input value={section.ctaUrl} onChange={(v) => updateSection(section.id, { ctaUrl: v })} placeholder="https://…" />
          </Field>
        </>
      )}

      {/* Mobile overrides */}
      <SectionLabel>Mobile Overrides</SectionLabel>
      <Field>
        <Label>Font Size Override</Label>
        <Input value={(mo.fontSize || '').replace('px', '')} type="number" min={10} max={72}
          onChange={(v) => setMobile('fontSize')(v ? `${v}px` : null)} placeholder="default" />
      </Field>
      <Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!mo.hidden} onChange={(e) => setMobile('hidden')(e.target.checked)} style={{ cursor: 'pointer' }} />
          Hide on mobile
        </label>
      </Field>
    </>
  );
}

// ── Section settings dispatcher ───────────────────────────────────────────────

const SECTION_TYPE_LABELS = {
  header:         'Header',
  footer:         'Footer',
  founders_focus: "Founder's Focus",
  meet_engineer:  'Meet the Engineer',
  appreciation:   'Appreciation',
  project_update: 'Project Update',
  text:           'Text',
  image:          'Image',
  hero:           'Hero Banner',
  cta:            'CTA Button',
  columns:        'Two Columns',
  divider:        'Divider',
  spacer:         'Spacer',
};

function SectionSettings({ section }) {
  const { updateSectionSettings, updateSection } = useEditorStore();
  const setSetting = (key) => (val) => updateSectionSettings(section.id, { [key]: val });

  const typeLabel = SECTION_TYPE_LABELS[section.type] || section.type;

  return (
    <div>
      <p style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
        Section: <span style={{ color: '#f54b01' }}>{typeLabel}</span>
      </p>

      {/* Desktop / Mobile toggle (visual only for now) */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        <button style={{ flex: 1, padding: '5px 8px', fontSize: 11, border: '1px solid var(--em-border)', borderRadius: 6, background: 'var(--em-bg-head)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <Monitor size={12} /> Desktop
        </button>
        <button style={{ flex: 1, padding: '5px 8px', fontSize: 11, border: '1px solid var(--em-border)', borderRadius: 6, background: 'var(--em-bg-head)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <Smartphone size={12} /> Mobile
        </button>
      </div>

      {/* Route to type-specific settings */}
      {section.type === 'header'         && <HeaderSettings         section={section} setSetting={setSetting} updateSection={updateSection} />}
      {section.type === 'founders_focus' && <FoundersFocusSettings  section={section} setSetting={setSetting} updateSection={updateSection} />}
      {section.type === 'meet_engineer'  && <MeetEngineerSettings   section={section} setSetting={setSetting} updateSection={updateSection} />}
      {section.type === 'appreciation'   && <AppreciationSettings   section={section} setSetting={setSetting} updateSection={updateSection} />}
      {section.type === 'project_update' && <ProjectUpdateSettings  section={section} setSetting={setSetting} updateSection={updateSection} />}
      {section.type === 'footer'         && <FooterSettings         section={section} setSetting={setSetting} updateSection={updateSection} />}
      {section.type === 'image'          && <ImageSettings          section={section} setSetting={setSetting} updateSection={updateSection} />}

      {/* Legacy types */}
      {!['header','founders_focus','meet_engineer','appreciation','project_update','footer','image'].includes(section.type) && (
        <LegacySectionSettings section={section} setSetting={setSetting} updateSection={updateSection} />
      )}
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export function SettingsPanel() {
  const { sections, selectedSectionId } = useEditorStore();
  const selectedSection = sections.find((s) => s.id === selectedSectionId);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Sticky header */}
      <div style={{
        padding: '14px 16px', borderBottom: '1px solid var(--em-border)',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        <Settings size={15} color="#6b7280" />
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' }}>
          {selectedSection ? 'Section Settings' : 'Newsletter Settings'}
        </h3>
      </div>
      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {selectedSection
          ? <SectionSettings section={selectedSection} />
          : <NewsletterSettings />
        }
      </div>
    </div>
  );
}
